import type { ClientGame, ClientGameCtx } from "./index";
import { ITEMS_BY_ID, titleOf } from "../../shared/catalog";
import { artSvg } from "../art";
import { sound } from "../sound";

export class ShredClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private root!: HTMLDivElement;
  private stage!: HTMLDivElement;
  private artBox!: HTMLDivElement;
  private barYou!: HTMLElement;
  private barRival!: HTMLElement;
  private barMid!: HTMLElement;
  private head!: HTMLElement;
  private hint!: HTMLElement;

  private pending = 0;
  private flush = 0;
  private lastRound = -1;
  private lastPhase = "";
  private onKey = (e: KeyboardEvent) => {
    if (e.code === "Space") { this.tap(); e.preventDefault(); }
  };

  private get ss() { return this.ctx.room.state.shred; }
  private get rivalSlot() { return this.ctx.mySlot === 0 ? 1 : 0; }

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    sound.unlock();
    this.root = document.createElement("div");
    this.root.className = "shred-wrap";
    this.root.innerHTML =
      `<div class="shred-head" id="shHead"></div>
       <div class="shred-stage" id="shStage">
         <div class="shred-art" id="shArt"></div>
       </div>
       <div class="shred-bar">
         <span class="b you" id="shYou"></span>
         <span class="b mid" id="shMid"></span>
         <span class="b rival" id="shRival"></span>
       </div>
       <div class="shred-hint" id="shHint">MASH</div>`;
    ctx.root.appendChild(this.root);
    this.stage = this.root.querySelector("#shStage")!;
    this.artBox = this.root.querySelector("#shArt")!;
    this.barYou = this.root.querySelector("#shYou")!;
    this.barMid = this.root.querySelector("#shMid")!;
    this.barRival = this.root.querySelector("#shRival")!;
    this.head = this.root.querySelector("#shHead")!;
    this.hint = this.root.querySelector("#shHint")!;

    const onDown = (e: Event) => { e.preventDefault(); this.tap(); };
    this.stage.addEventListener("touchstart", onDown, { passive: false });
    this.stage.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", this.onKey);

    ctx.room.onMessage("shred-break", (m: any) => {
      sound.hit();
      try { navigator.vibrate?.([20, 40, 60]); } catch { /* ignore */ }
      this.stage.classList.add("broke");
      if (m?.slot === this.ctx.mySlot) sound.cheer(); else sound.groan();
    });

    this.flush = window.setInterval(() => this.sendTaps(), 110);
    this.sync();
  }

  unmount() {
    clearInterval(this.flush);
    window.removeEventListener("keydown", this.onKey);
  }

  private tap() {
    if (this.ss.phase !== "smash" || this.ctx.room.state.phase !== "playing") return;
    this.pending++;
    sound.swing();
    this.stage.classList.remove("hit");
    void this.stage.offsetWidth;
    this.stage.classList.add("hit");
  }

  private sendTaps() {
    if (this.pending <= 0) return;
    this.ctx.room.send("smash", { taps: this.pending });
    this.pending = 0;
  }

  sync() {
    const ss = this.ss;
    const phase = this.ctx.room.state.phase;

    if (phase === "countdown") {
      const n = Math.ceil((this.ctx.room.state.countdown ?? 0) / 1000);
      this.head.textContent = "";
      this.hint.textContent = n > 0 ? `${n}` : "GO";
      return;
    }

    // new round -> rebuild art
    if (ss.round !== this.lastRound || ss.itemId && !this.artBox.dataset.id) {
      this.lastRound = ss.round;
      const it = ITEMS_BY_ID[ss.itemId];
      this.artBox.dataset.id = ss.itemId;
      this.artBox.innerHTML = it ? artSvg(it) : "";
      this.stage.classList.remove("broke");
      this.spawnRound(it ? titleOf(it) : "");
    }

    const mine = ss.scores?.[this.ctx.mySlot] ?? 0;
    const theirs = ss.scores?.[this.rivalSlot] ?? 0;
    const it = ITEMS_BY_ID[ss.itemId];
    this.head.innerHTML =
      `<span>round ${ss.round}/${ss.totalRounds}</span>` +
      `<span class="nm">${it ? esc(titleOf(it)) : ""}</span>` +
      `<span><b>${mine}</b>&ndash;<b>${theirs}</b></span>`;

    const max = ss.maxHp || 1;
    const yd = Math.min(max, ss.damage?.[this.ctx.mySlot] ?? 0);
    const rd = Math.min(max, ss.damage?.[this.rivalSlot] ?? 0);
    const rem = Math.max(0, max - yd - rd);
    this.barYou.style.flexGrow = String(yd);
    this.barMid.style.flexGrow = String(rem);
    this.barRival.style.flexGrow = String(rd);

    // damage cues on the art
    const gone = 1 - rem / max;
    this.artBox.style.filter =
      gone > 0.66 ? "grayscale(.3) brightness(.8)" : gone > 0.33 ? "brightness(.92)" : "";
    this.artBox.classList.toggle("crackA", gone > 0.35);
    this.artBox.classList.toggle("crackB", gone > 0.7);

    if (ss.phase === "break" && this.lastPhase !== "break") {
      const won = ss.roundWinner === this.ctx.mySlot;
      this.hint.textContent = won ? "YOU SHREDDED IT" : "RIVAL SHREDDED IT";
      this.hint.classList.toggle("win", won);
      this.hint.classList.toggle("lose", !won);
    } else if (ss.phase === "smash") {
      this.hint.textContent = "MASH";
      this.hint.classList.remove("win", "lose");
    }
    this.lastPhase = ss.phase;
  }

  private spawnRound(_name: string) {
    this.barYou.style.flexGrow = "0";
    this.barRival.style.flexGrow = "0";
    this.barMid.style.flexGrow = "1";
  }

  results(ctx: ClientGameCtx): HTMLElement {
    this.ctx = ctx;
    const ss = ctx.room.state.shred;
    const mine = ss.scores?.[ctx.mySlot] ?? 0;
    const theirs = ss.scores?.[ctx.mySlot === 0 ? 1 : 0] ?? 0;
    const d = document.createElement("div");
    d.className = "arc-final";
    d.innerHTML = `<div class="score-big">${mine} : ${theirs}</div><div class="arc-note">rounds shredded</div>`;
    return d;
  }
}

function esc(s: string) {
  return String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
}
